import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { getCookie } from '@auth/bp-pf-auth';
import { Observable, Subscription, catchError, filter, finalize, interval, map, of, shareReplay, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CommonService } from '../common/common.service';
import { RoleService } from '../others/role/role.service';
import { ToastNotificationService } from '../others/toast-notification/toast-notification.service';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import {
  EDIT_LOCK_CLIENT_SESSION_KEY,
  EDIT_LOCK_EXEMPT_STATUSES,
  EDIT_LOCK_EXPIRED_ERROR_TYPE,
  EDIT_LOCK_EXPIRED_MESSAGE,
  EDIT_LOCK_LOCKABLE_STATUSES,
  EDIT_LOCK_ROUTE_FLAG,
   EDIT_LOCK_HTTP_STATUS_BAD_REQUEST,
  EDIT_LOCK_HTTP_STATUS_CONFLICT,
} from './submission-edit-lock.constants';
import {
  AcquireEditLockResponse,
  EditLockOutcome,
  EditLockState,
  ReleaseEditLockResponse
} from './submission-edit-lock.types';
import { UserRole } from '../data/model/user-role.model';
import { SubmissionsService } from '../submissions/submissions.service';

/** Normalizes a status for comparison (upper case, `_` treated as `-` so INPROGRESS_HITL matches INPROGRESS-HITL). */
const normalizeStatus = (status: string): string => status.trim().toUpperCase().replace(/_/g, '-');
const EXEMPT_STATUSES = new Set(EDIT_LOCK_EXEMPT_STATUSES.map(normalizeStatus));

const DEFAULT_HEARTBEAT_SECONDS = 60;

/** Acquire abandoned because the user left the page or moved to another submission; callers show nothing. */
const CANCELLED_OUTCOME: EditLockOutcome = { ok: false, status: 0, cancelled: true };

/**
 * 409 with data.error_type EDIT_LOCK_EXPIRED: the token no longer exists server-side (e.g. a duplicate tab of
 * the same user released it). Only this error triggers a quiet re-acquire / save retry; SUBMISSION_LOCKED and
 * every other error never do.
 */
export function isLockExpiredError(error: HttpErrorResponse | null | undefined): boolean {
 return error?.status === EDIT_LOCK_HTTP_STATUS_CONFLICT && error?.error?.data?.error_type === EDIT_LOCK_EXPIRED_ERROR_TYPE;
}

/** App roles mapped to the values the lock API expects in x-user-roles. */
const ROLE_HEADER_MAP: Record<UserRole, string> = {
  agent: 'AGENT',
  underwriter: 'UNDERWRITER',
  uw_assist: 'UW_ASSISTANT'
};

@Injectable({ providedIn: 'root' })
export class SubmissionEditLockService implements OnDestroy {
  readonly lockUrl = `${environment.api_url}submission/edit-lock`;
  readonly heartbeatUrl = `${environment.api_url}submission/edit-lock/heartbeat`;

  private state: EditLockState | null = null;
  private pendingAcquire?: { submissionId: string; txnId: string; request$: Observable<EditLockOutcome> };
  /** In-flight quiet re-acquire after EDIT_LOCK_EXPIRED; heartbeat and saves share this one request. */
  private reacquire$?: Observable<boolean>;
  /** Bumped whenever the tracked lock is reset, so late re-acquire responses can tell they are stale. */
  private stateVersion = 0;
  private heartbeatSub?: Subscription;
  private routerSub?: Subscription;
  private lostModalOpen = false;
  /** Submission whose view-submission status is exempt from locking (no acquire, no token header). */
  private exemptSubmissionId: string | null = null;
  private readonly pageHideHandler = () => this.releaseOnPageHide();

  constructor(
    private http: HttpClient,
    private router: Router,
    private zone: NgZone,
    private modalService: NgbModal,
    private toast: ToastNotificationService,
    private commonService: CommonService,
    private roleService: RoleService,
    private submissionsService: SubmissionsService
  ) { }

  /** Random id for this browser tab; sessionStorage keeps it across reloads of the same tab. */
  getClientSessionId(): string {
    let id = sessionStorage.getItem(EDIT_LOCK_CLIENT_SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(EDIT_LOCK_CLIENT_SESSION_KEY, id);
    }
    return id;
  }

  /** Backend locks for agents, underwriters and UW assistants; any other role keeps the unlocked flow. */
  canLock(): boolean {
    return !!this.roleService.role && this.roleService.role in ROLE_HEADER_MAP;
  }

  /** Only DRAFT and INDICATIVE submissions can be locked. */
  isLockableStatus(status?: string | null): boolean {
    return !!status && EDIT_LOCK_LOCKABLE_STATUSES.includes(status.toUpperCase());
  }

  /** INPROGRESS-HITL, UW_REVIEW and UW_REQUOTE submissions never take an edit lock. */
  isExemptStatus(status?: string | null): boolean {
    return !!status && EXEMPT_STATUSES.has(normalizeStatus(status));
  }

  /** True while the current submission is in an exempt status: no lock call and no token header. */
  isExempt(): boolean {
    return !!this.exemptSubmissionId;
  }

  isExemptFor(submissionId: string): boolean {
    return !!submissionId && this.exemptSubmissionId === submissionId;
  }

  /** Status as reported by the view-submission endpoint; null when it cannot be read. */
  fetchViewSubmissionStatus(submissionId: string): Observable<string | null> {
    return this.submissionsService.getSubmissionDetails(submissionId).pipe(
      map((res: any) => (res?.data?.status as string | undefined) ?? null),
      catchError(err => {
        console.error('Failed to fetch submission status for edit lock.', err);
        return of(null);
      })
    );
  }

  /** Records the submission's view-submission status; an exempt status drops any held lock and its token. */
  applySubmissionStatus(submissionId: string, status?: string | null): void {
    if (this.isExemptStatus(status)) {
      if (this.state) {
        this.release();
      }
      this.pendingAcquire = undefined;
      this.exemptSubmissionId = submissionId;
    } else if (this.exemptSubmissionId === submissionId) {
      this.exemptSubmissionId = null;
    }
  }

  /**
   * Reads the status from view-submission first; exempt statuses (and, with requireLockable,
   * anything other than DRAFT/INDICATIVE) skip the edit-lock call entirely.
   */
  acquireIfEditable(
    submissionId: string,
    txnId: string,
    options: { requireLockable?: boolean } = {}
  ): Observable<EditLockOutcome> {
    return this.fetchViewSubmissionStatus(submissionId).pipe(
      switchMap(status => {
        this.applySubmissionStatus(submissionId, status);
        if (this.isExemptStatus(status) || (options.requireLockable && !this.isLockableStatus(status))) {
          return of({ ok: true, skipped: true } as EditLockOutcome);
        }
        return this.acquire(submissionId, txnId);
      })
    );
  }

  getToken(): string | null {
    if (this.isExempt()) return null;
    return this.state?.status === 'held' ? this.state.lockToken : null;
  }

  isLost(): boolean {
    return this.state?.status === 'lost';
  }

  isHeldFor(txnId: string): boolean {
    return !!this.state && this.state.status !== 'lost' && this.state.txnId === txnId;
  }

  acquire(submissionId: string, txnId: string): Observable<EditLockOutcome> {
    if (this.exemptSubmissionId && this.exemptSubmissionId !== submissionId) {
      this.exemptSubmissionId = null;
    }
    // Backstop: never lock a submission already known to be INPROGRESS-HITL / UW_REVIEW / UW_REQUOTE.
    if (this.isExemptFor(submissionId)) {
      return of({ ok: true, skipped: true });
    }
    if (this.isHeldFor(txnId)) {
      this.state!.submissionId = submissionId;
      return of({ ok: true });
    }
    if (this.pendingAcquire?.txnId === txnId) {
      this.pendingAcquire.submissionId = submissionId;
      return this.pendingAcquire.request$;
    }
    // Drops the held lock and supersedes any acquire still in flight for another submission.
    if (this.state || this.pendingAcquire) {
      this.release();
    }
    // The user left the edit flow before we got here (e.g. while view-submission was loading): don't lock.
    if (!this.isPermittedRoute(this.router.routerState.snapshot.root, submissionId)) {
      return of(CANCELLED_OUTCOME);
    }

    // A reply that arrives after this acquire was superseded (navigation away, another submission, exempt
    // status, release) must not become the tracked lock: give its token straight back instead.
    const isCurrent = () => this.pendingAcquire?.request$ === request$;
    const request$: Observable<EditLockOutcome> = this.postAcquire(txnId).pipe(
      map(res => {
        if (!isCurrent()) {
          this.sendRelease(txnId, res?.data?.lock_token ?? null);
          return CANCELLED_OUTCOME;
        }
        this.applyAcquired(submissionId, txnId, res);
        return { ok: true } as EditLockOutcome;
      }),
      catchError((error: HttpErrorResponse) => of(isCurrent() ? this.toFailure(error) : CANCELLED_OUTCOME)),
      finalize(() => {
        if (this.pendingAcquire?.request$ === request$) {
          this.pendingAcquire = undefined;
        }
      }),
      shareReplay(1)
    );
    this.pendingAcquire = { submissionId, txnId, request$ };
    // Watch navigation while the request is in flight too, so leaving the page cancels it.
    this.ensureListeners();
    return request$;
  }

  /**
   * Quietly takes a fresh lock after EDIT_LOCK_EXPIRED (e.g. a duplicate tab released the shared token).
   * Stops the heartbeat and drops the dead token without a release, then acquires with the same txn_id and
   * this tab's client_session_id. Concurrent callers share one request. Emits true when a new token is held.
   */
  reacquire(): Observable<boolean> {
    if (this.reacquire$) return this.reacquire$;
    const current = this.state;
    if (!current || current.status === 'lost' || this.isExemptFor(current.submissionId)) {
      return of(false);
    }

    // Keep tracking the lock (same submission/txn) with its token cleared; navigation still resets it normally.
    this.stopHeartbeat();
    current.lockToken = null;
    const { submissionId, txnId } = current;
    const version = this.stateVersion;
    const stillOurs = () => this.stateVersion === version && this.state?.txnId === txnId;

    const request$ = this.postAcquire(txnId).pipe(
      map(res => {
        if (!stillOurs()) {
          // User left / opened another submission meanwhile: give the new token back, leave the current lock alone.
          this.sendRelease(txnId, res?.data?.lock_token ?? null);
          return false;
        }
        this.applyAcquired(submissionId, txnId, res);
        return this.getToken() !== null;
      }),
      catchError((error: HttpErrorResponse) => {
        if (stillOurs()) {
          this.handleReacquireFailure(this.toFailure(error));
        }
        return of(false);
      }),
      finalize(() => {
        if (this.reacquire$ === request$) {
          this.reacquire$ = undefined;
        }
      }),
      shareReplay(1)
    );
    this.reacquire$ = request$;
    return request$;
  }

  /** The re-acquire in flight, if any, so protected saves can wait for the new token. */
  pendingReacquire(): Observable<boolean> | null {
    return this.reacquire$ ?? null;
  }

  /** 409 opens the lock conflict modal; every other failure follows the toast + dashboard pattern. */
  handleAcquireError(outcome: EditLockOutcome, options: { redirect?: boolean } = {}): void {
    // Cancelled: the user already left; no modal, toast or redirect.
    if (outcome.ok || outcome.cancelled) return;
    if (outcome.status === EDIT_LOCK_HTTP_STATUS_CONFLICT) {
      this.openGoToDashboardDialog(
        'Submission Locked',
        outcome.message || 'The submission is locked by another user. Please wait for the submission to be unlocked to edit further'
      );
      return;
    }
    this.toast.showError(
      outcome.message || 'Failed to acquire submission edit lock',
      'Unable to edit submission'
    );
    if (options.redirect !== false) {
      this.commonService.navigateToURLWithtenantCode('dashboard');
    }
  }

  /** Called when a protected save is rejected because the lock is gone. */
  notifyLockExpired(message?: string): void {
    this.markLost(message);
  }

  release(): void {
    const current = this.state;
    this.clearState();
    if (!current?.lockToken) return;
    this.sendRelease(current.txnId, current.lockToken);
  }

  /** Best-effort release while the tab is closing; HttpClient cannot send keepalive requests. */
  releaseOnPageHide(): void {
    const current = this.state;
    if (!current?.lockToken) return;
    this.clearState();

    try {
      const httpHeaders = this.buildHeaders();
      const headers: Record<string, string> = {};
      httpHeaders.keys().forEach(key => headers[key] = httpHeaders.get(key) || '');
      const accessToken = this.getAccessToken();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      fetch(this.lockUrl, {
        method: 'DELETE',
        keepalive: true,
        headers,
        body: JSON.stringify({ txn_id: current.txnId, lock_token: current.lockToken })
      }).catch(() => undefined);
    } catch (err) {
      console.error('Failed to release submission edit lock on page hide.', err);
    }
  }

  ngOnDestroy(): void {
    this.clearState();
  }

  private buildHeaders(): HttpHeaders {
    const roles = [this.roleService.role, ...this.roleService.roles.filter(r => r !== this.roleService.role)]
      .filter(Boolean)
      .map(role => ROLE_HEADER_MAP[role] ?? role.toUpperCase())
      .join(',');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'tenant-id': environment.tenant_id,
      'app-id': environment.app_id,
      'x-email-id': this.commonService.getEmail(),
      'x-user-roles': roles
    });
  }

  private postAcquire(txnId: string): Observable<AcquireEditLockResponse> {
    const body = { txn_id: txnId, client_session_id: this.getClientSessionId() };
    return this.http.post<AcquireEditLockResponse>(this.lockUrl, body, { headers: this.buildHeaders() });
  }

  /** Stores the acquired token as the tracked lock and (re)starts the heartbeat with it. */
  private applyAcquired(submissionId: string, txnId: string, res: AcquireEditLockResponse): void {
    const lockToken = res?.data?.lock_token ?? null;
    this.state = {
      submissionId,
      txnId,
      lockToken,
      status: lockToken ? 'held' : 'disabled'
    };
    this.lostModalOpen = false;
    if (lockToken) {
      this.startHeartbeat(res.data?.heartbeat_interval_seconds || DEFAULT_HEARTBEAT_SECONDS);
    }
    this.ensureListeners();
  }

  private sendRelease(txnId: string, lockToken: string | null): void {
    if (!lockToken) return;
    this.http
      .delete<ReleaseEditLockResponse>(this.lockUrl, { body: { txn_id: txnId, lock_token: lockToken }, headers: this.buildHeaders() })
      .subscribe({
        // data.released === false means the token no longer held the lock; nothing else to do.
        error: err => console.error('Failed to release submission edit lock.', err)
      });
  }

  private toFailure(error: HttpErrorResponse): EditLockOutcome {
    return {
      ok: false,
      status: error?.status ?? 0,
      message: error?.error?.message || error?.message
    };
  }

  /** 409 (SUBMISSION_LOCKED: another user took it in the gap) opens "Submission Locked"; others toast + dashboard. */
  private handleReacquireFailure(outcome: EditLockOutcome): void {
    if (this.state) {
      this.state.status = 'lost';
      this.state.lockToken = null;
    }
    this.stopHeartbeat();
    if (this.lostModalOpen) return;
    this.lostModalOpen = true;
    this.handleAcquireError(outcome);
  }

  private startHeartbeat(seconds: number): void {
    this.stopHeartbeat();
    // Timer runs outside Angular so it does not keep the app "unstable"; results re-enter the zone.
    // Heartbeats are plain HTTP calls, so they never count as user activity for the 2-hour inactivity logout.
    this.zone.runOutsideAngular(() => {
      this.heartbeatSub = interval(seconds * 1000)
        .pipe(
          switchMap(() => {
            const current = this.state;
            if (!current?.lockToken) return of(null);
            if (!this.getAccessToken()) {
              // Logged out (e.g. inactivity logout): stop heartbeating and let the lock lapse.
              this.stopHeartbeat();
              return of(null);
            }
            const body = { txn_id: current.txnId, lock_token: current.lockToken };
            return this.http.put(this.heartbeatUrl, body, { headers: this.buildHeaders() }).pipe(
              catchError((error: HttpErrorResponse) => {
                this.zone.run(() => this.handleHeartbeatError(error));
                return of(null);
              })
            );
          })
        )
        .subscribe();
    });
  }

  private handleHeartbeatError(error: HttpErrorResponse): void {
    if (isLockExpiredError(error)) {
      this.reacquire().subscribe();
      return;
    }
    if (error?.status === EDIT_LOCK_HTTP_STATUS_CONFLICT || error?.status === EDIT_LOCK_HTTP_STATUS_BAD_REQUEST) {
      this.markLost(error?.error?.message);
      return;
    }
    // Transient failure (500/network): the lock survives 5 minutes, so the next tick retries.
    console.error('Submission edit lock heartbeat failed.', error);
  }

  private stopHeartbeat(): void {
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = undefined;
  }

  private markLost(message?: string): void {
    if (this.state) {
      this.state.status = 'lost';
      this.state.lockToken = null;
    }
    this.stopHeartbeat();
    if (this.lostModalOpen) return;
    this.lostModalOpen = true;
    this.openGoToDashboardDialog('Edit Session Expired', message || EDIT_LOCK_EXPIRED_MESSAGE);
  }

  private openGoToDashboardDialog(title: string, message: string): void {
    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md'
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.isHtml = false;
    modalRef.componentInstance.confirmText = 'Go to Dashboard';
    modalRef.componentInstance.showCancel = false;
    modalRef.result
      .then(() => this.commonService.navigateToURLWithtenantCode('dashboard'))
      .catch(() => this.commonService.navigateToURLWithtenantCode('dashboard'));
  }

  private ensureListeners(): void {
    if (!this.routerSub) {
      this.routerSub = this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          if (!this.isPermittedRoute(this.router.routerState.snapshot.root)) {
            this.release();
          }
        });
    }
    window.removeEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  /**
   * Permitted = route flagged as part of the edit/review flow and (when it has an id) for the locked submission,
   * or for the submission whose acquire is still in flight.
   */
  private isPermittedRoute(
    root: ActivatedRouteSnapshot,
    submissionId: string | undefined = this.state?.submissionId ?? this.pendingAcquire?.submissionId
  ): boolean {
    let leaf = root;
    while (leaf.firstChild) {
      leaf = leaf.firstChild;
    }
    if (!leaf.data?.[EDIT_LOCK_ROUTE_FLAG]) return false;
    const routeId = leaf.paramMap.get('id');
    return !routeId || routeId === submissionId;
  }

  private clearState(): void {
    this.state = null;
    this.pendingAcquire = undefined;
    this.reacquire$ = undefined;
    this.stateVersion++;
    this.stopHeartbeat();
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
    window.removeEventListener('pagehide', this.pageHideHandler);
  }

  private getAccessToken(): string | null {
    const tenant = getCookie(`${environment.app_id}_tenant_code`);
    return tenant ? getCookie(`${tenant}_${environment.app_id}_access_token`) : null;
  }
}
